import { clientContactIds } from '../clientContactSelection';
import { useState, useCallback } from 'react';
import { Modal as AntModal, message, Alert } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import nodeAPI from '../../../../service/ApiServiceNode';
import { Modal } from '../../../../design-system';
import useRole from '../../../../hooks/useRole';
import logger from '../../../../utils/logger';
import { parseAccountingDate } from '../../../../utils/dateUtils';
import { normalizeEditableExternalProjectLinks } from '../utils/projectExternalLinks';
import { findClientById, resolveClientAutoFillUpdates } from '../utils/clientAutoFill';
import { canArchiveProjectFromDetail } from '../utils/projectArchiveStatus';



const LOST_FIELDS = [
    'lead',
    'leadFailDate',
    'leadFailNote',
    'leadFail',
    'proposalType',
    'proposalPresentation',
    'presentationParticipant',
    'withDecisionMaker',
    'mainReason',
    'subReason'
];

const isEmptyProjectStatus = (status) => (
    status === null ||
    status === undefined ||
    (typeof status === 'string' && status.trim() === '')
);

export const omitEmptyProjectStatus = (submitData) => {
    const normalizedData = { ...submitData };

    if (isEmptyProjectStatus(normalizedData.status)) {
        delete normalizedData.status;
    }

    return normalizedData;
};

export const buildProjectArchiveManagementPayload = (projectId, archiveDate = dayjs()) => ({
    project_id: projectId,
    archive_date: dayjs(archiveDate).format('YYYY-MM-DD'),
    reason: 'project_detail'
});

export const archiveProjectViaManagementApi = (projectId, archiveDate = dayjs()) => (
    nodeAPI.post(
        '/report/project-archive-management/archive',
        buildProjectArchiveManagementPayload(projectId, archiveDate)
    )
);

export const unarchiveProjectViaManagementApi = (projectId, reversalDate = null) => {
    const payload = { project_id: projectId };

    if (reversalDate) {
        payload.reversal_date = dayjs(reversalDate).format('YYYY-MM-DD');
    }

    return nodeAPI.post('/report/project-archive-management/unarchive', payload);
};

export const normalizeClientChangeArgs = (
    onOpenDropdownOrLegacyMode = null,
    isInitialLoadOrOnOpenDropdown = false,
    legacyIsInitialLoad = false
) => {
    if (typeof onOpenDropdownOrLegacyMode === 'boolean') {
        return {
            onOpenDropdown: typeof isInitialLoadOrOnOpenDropdown === 'function' ? isInitialLoadOrOnOpenDropdown : null,
            isInitialLoad: legacyIsInitialLoad === true
        };
    }

    return {
        onOpenDropdown: typeof onOpenDropdownOrLegacyMode === 'function' ? onOpenDropdownOrLegacyMode : null,
        isInitialLoad: isInitialLoadOrOnOpenDropdown === true
    };
};

/**
 * Shared hook for project form functionality
 * Used by both Create Project and Edit Project forms
 */
export const useProjectForm = (form, initialProject = null) => {
    // Role-based permissions
    const { hasRole, ROLES } = useRole();

    // Form control states
    const [autoFilledFields, setAutoFilledFields] = useState(new Set());
    const [leadStatus, setLeadStatus] = useState(initialProject?.lead || null);
    const [selectedClient, setSelectedClient] = useState(initialProject?.client?.id || initialProject?.client_id || null);
    const [noPresentation, setNoPresentation] = useState(initialProject?.no_presentation || false);
    const [noVendorCost, setNoVendorCost] = useState(initialProject?.no_estimated_vendor_cost || false);

    // Helper to check if user can bypass vendor bill validation
    const canBypassArchivalValidation = useCallback(() => {
        return (
            hasRole(ROLES.ROLE_FINANCE) ||
            hasRole(ROLES.ROLE_MANAGEMENT) ||
            hasRole(ROLES.ROLE_OWNER)
        );
    }, [ROLES.ROLE_FINANCE, ROLES.ROLE_MANAGEMENT, ROLES.ROLE_OWNER, hasRole]);

    // Track referral input state
    const [showReferralInput, setShowReferralInput] = useState(false);
    const [referralName, setReferralName] = useState('');

    // Handle manual field changes to remove auto-filled status
    const handleManualFieldChange = (fieldName) => {
        if (autoFilledFields.has(fieldName)) {
            const newAutoFilled = new Set(autoFilledFields);
            newAutoFilled.delete(fieldName);
            setAutoFilledFields(newAutoFilled);
        }
    };

    // Handle lead source change to show/hide referral input
    const handleLeadSourceChange = (value) => {
        handleManualFieldChange('leadSource');

        if (value === 'Referral' || value === 'referral') {
            setShowReferralInput(true);
            // Parse existing referral name if editing
            const currentValue = form.getFieldValue('leadSource');
            if (currentValue && currentValue.startsWith('Referral - ')) {
                setReferralName(currentValue.substring(11));
            }
        } else {
            setShowReferralInput(false);
            setReferralName('');
        }
    };

    // Handle client change with cascading effects - EXACT copy from ClientProjectForm
    const handleClientChange = async (
        clientId,
        clients,
        setClientContacts,
        onOpenDropdownOrLegacyMode = null,
        isInitialLoadOrOnOpenDropdown = false,
        legacyIsInitialLoad = false
    ) => {
        const { onOpenDropdown, isInitialLoad } = normalizeClientChangeArgs(
            onOpenDropdownOrLegacyMode,
            isInitialLoadOrOnOpenDropdown,
            legacyIsInitialLoad
        );

        setSelectedClient(clientId);
        // In initial load, don't reset the clientPersonInCharge as it might contain the saved value
        if (!isInitialLoad) {
            form.setFieldsValue({ clientPersonInCharge: undefined }); // Reset client contact selection
        }
        if (setClientContacts) setClientContacts([]); // Clear previous contacts if function provided
        
        // Store the auto-filled updates to apply after contacts are fetched
        let pendingAutoFilled = new Set(autoFilledFields);
        
        // Clear any existing clientPersonInCharge auto-fill marker when changing clients
        // It will be re-added only if there's actual auto-selection (default or single contact)
        pendingAutoFilled.delete('clientPersonInCharge');
        
        // Check if client is confirmed and auto-set lead source and industry
        const selectedClientData = findClientById(clients, clientId);
        const autoFillUpdates = resolveClientAutoFillUpdates(selectedClientData, pendingAutoFilled);
        if (Object.keys(autoFillUpdates.fieldValues).length > 0) {
            form.setFieldsValue(autoFillUpdates.fieldValues);
        }
        pendingAutoFilled = autoFillUpdates.autoFilledFields;
        
        // Fetch client contacts
        if (clientId) {
            try {
                // Use the new pagination-free endpoint
                const response = await nodeAPI.get(`/project/clients/${clientId}/contacts?view=dropdown`);
                
                // Handle various response structures
                const responseData = response.data?.data || response.data || {};
                const contacts = responseData.contacts || responseData.data || responseData || [];
                
                
                // Format contacts properly
                const formattedContacts = Array.isArray(contacts) 
                    ? contacts.map(contact => ({
                        id: contact.id || contact.contact_id,
                        firstName: contact.first_name || contact.firstName || '',
                        lastName: contact.last_name || contact.lastName || '',
                        email: contact.email || '',
                        phone: contact.phone || contact.mobile || '',
                        position: contact.position || contact.title || '',
                        fullName: contact.full_name || `${contact.first_name || contact.firstName || ''} ${contact.last_name || contact.lastName || ''}`.trim(),
                        isDefault: contact.is_default || false
                    }))
                    : [];
                    
                if (setClientContacts) setClientContacts(formattedContacts);
                
                // Check current client PIC value 
                const currentClientPIC = form.getFieldValue('clientPersonInCharge');
                
                // Auto-select default contact if exists, or single contact
                const defaultContact = formattedContacts.find(c => c.isDefault);
                const hasDefaultContact = !!defaultContact;
                const hasMultipleContacts = formattedContacts.length > 1;
                const hasSingleContact = formattedContacts.length === 1;
                
                if (defaultContact && !currentClientPIC?.length) {
                    // Auto-select default contact when no current selection (both create and edit modes)
                    form.setFieldsValue({ clientPersonInCharge: [defaultContact.id] });
                    // Track as auto-filled ONLY if it's not initial load in edit mode
                    if (!isInitialLoad) {
                        pendingAutoFilled.add('clientPersonInCharge');
                    }
                } else if (hasSingleContact && !currentClientPIC?.length) {
                    // Auto-select single contact when no current selection (both create and edit modes)
                    form.setFieldsValue({ clientPersonInCharge: [formattedContacts[0].id] });
                    // Track as auto-filled ONLY if it's not initial load in edit mode
                    if (!isInitialLoad) {
                        pendingAutoFilled.add('clientPersonInCharge');
                    }
                } else {
                    // Multiple contacts but no default, or already has selection - handle dropdown expansion
                    if (pendingAutoFilled.has('clientPersonInCharge')) {
                        // Remove auto-filled status when no auto-selection is happening
                        pendingAutoFilled.delete('clientPersonInCharge');
                    }
                }
                
                // Auto-expand dropdown logic (for both create and edit modes)
                // BUT NOT on initial load of edit mode (when user clicks Edit button)
                const shouldExpandDropdown = (
                    hasMultipleContacts &&
                    !hasDefaultContact &&
                    !currentClientPIC?.length &&
                    onOpenDropdown &&
                    !isInitialLoad  // Don't auto-open when just entering edit mode
                );

                if (shouldExpandDropdown) {
                    setTimeout(() => onOpenDropdown(true), 100);
                }
                
                // Apply all auto-filled updates at once
                setAutoFilledFields(pendingAutoFilled);
            } catch (error) {
                logger.error('Error fetching client contacts:', error);
                if (setClientContacts) setClientContacts([]);
                
                // Apply auto-filled updates even if contact fetch fails
                setAutoFilledFields(pendingAutoFilled);
            }
        } else {
            // Apply auto-filled updates even if no clientId
            setAutoFilledFields(pendingAutoFilled);
        }
    };

    // Handle lead status change - EXACT copy from ClientProjectForm (handleLeadChange)
    const handleLeadChange = (value, leadStatuses) => {
        setLeadStatus(value);
        
        // Find the selected lead status from the dynamic data
        const selectedStatus = Array.isArray(leadStatuses) ? leadStatuses.find(status => status.id === value) : null;

        // Create new set for auto-filled fields
        const newAutoFilled = new Set(autoFilledFields);

        // Set probability based on lead status (always auto-filled)
        if (selectedStatus && selectedStatus.probability !== undefined) {
            form.setFieldsValue({ probability: selectedStatus.probability });
            newAutoFilled.add('probability');
        }
        
        // Handle confirmed status based on lead status
        if (value === 8) {
            // Set status to In-Planning when lead is won
            form.setFieldsValue({ status: 'In-Planning' });
            newAutoFilled.add('status');
            
            // Also set win date to today when project is won
            form.setFieldsValue({ winDate: dayjs() });
            newAutoFilled.add('winDate');
        } else {
            // Clear status when lead status is less than 8 (only if auto-filled)
            if (autoFilledFields.has('status')) {
                form.setFieldsValue({ status: undefined });
                newAutoFilled.delete('status');
            }
            
            // Clear win date when lead status is not 8 (only if auto-filled)
            if (autoFilledFields.has('winDate')) {
                form.setFieldsValue({ winDate: undefined });
                newAutoFilled.delete('winDate');
            }
        }
        
        // Set dates based on lead status
        if (value === 1) {
            // For Lead (Qualified): Set lead date to today
            form.setFieldsValue({ leadDate: dayjs() });
            newAutoFilled.add('leadDate');
        }
        
        // Handle proposal date
        if (value >= 5) {
            // For lead status 5 and above: Set proposal date to today if empty
            if (!form.getFieldValue('proposalDate')) {
                form.setFieldsValue({ proposalDate: dayjs() });
                newAutoFilled.add('proposalDate');
            }
        } else if (value < 5) {
            // Clear proposal date if it was auto-filled and lead status is below 5
            if (autoFilledFields.has('proposalDate')) {
                form.setFieldsValue({ proposalDate: undefined });
                newAutoFilled.delete('proposalDate');
            }
        }
        
        // Update auto-filled fields tracking
        setAutoFilledFields(newAutoFilled);
        
        // Update required fields based on lead status
        form.validateFields();
    };

    // Date validation functions - EXACT copy from ClientProjectForm
    const validateDisabledDate = (currentField) => {
        return {
            validator: (_, value) => {
                if (!value || !dayjs.isDayjs(value) || !value.isValid()) {
                    return Promise.resolve(); // Skip validation if no value or invalid
                }
                
                // Use the same logic as getDisabledDate to check if this date would be disabled
                const isDisabled = getDisabledDate(currentField)(value);
                
                if (isDisabled) {
                    return Promise.reject(new Error('Date conflicts with project timeline'));
                }
                
                return Promise.resolve();
            }
        };
    };

    // Function to trigger validation on all date fields when any date changes
    const validateAllDates = () => {
        const dateFields = ['leadDate', 'proposalDate', 'proposalFollowUpDate', 'presentationDate', 'winDate', 'startDate', 'endDate'];
        dateFields.forEach(field => {
            form.validateFields([field]).catch(() => {}); // Catch to prevent unhandled promise rejection
        });
    };

    // Date validation helper function - Updated to skip proposalFollowUpDate-presentationDate validation
    const getDisabledDate = (currentField) => {
        return (currentDate) => {
            if (!currentDate) return false;

            try {
                const formValues = form.getFieldsValue();

                // Special case for win date: if it's disabled by the form, don't apply additional validation
                if (currentField === 'winDate' && leadStatus !== 8) {
                    return false; // Let the disabled prop handle it
                }

                // Special handling for proposalFollowUpDate - it's independent of presentationDate
                if (currentField === 'proposalFollowUpDate') {
                    // Only check against leadDate and proposalDate
                    const precedingFields = ['leadDate', 'proposalDate'];
                    for (const fieldName of precedingFields) {
                        const fieldValue = formValues[fieldName];
                        if (fieldValue &&
                            dayjs.isDayjs(fieldValue) &&
                            fieldValue.isValid() &&
                            currentDate.isBefore(fieldValue, 'day')) {
                            return true; // Disable this date because it's before a preceding field
                        }
                    }
                    // Don't check against presentationDate or later fields
                    return false;
                }

                // For presentationDate, skip checking against proposalFollowUpDate
                if (currentField === 'presentationDate') {
                    // Check against leadDate and proposalDate only
                    const precedingFields = ['leadDate', 'proposalDate'];
                    for (const fieldName of precedingFields) {
                        const fieldValue = formValues[fieldName];
                        if (fieldValue &&
                            dayjs.isDayjs(fieldValue) &&
                            fieldValue.isValid() &&
                            currentDate.isBefore(fieldValue, 'day')) {
                            return true;
                        }
                    }

                    // Check against following fields (winDate, startDate, endDate)
                    const followingFields = ['winDate', 'startDate', 'endDate'];
                    for (const fieldName of followingFields) {
                        const fieldValue = formValues[fieldName];

                        // Skip validation against auto-filled dates
                        if ((fieldName === 'winDate' || fieldName === 'startDate') &&
                            autoFilledFields.has(fieldName)) {
                            continue;
                        }

                        if (fieldValue &&
                            dayjs.isDayjs(fieldValue) &&
                            fieldValue.isValid() &&
                            currentDate.isAfter(fieldValue, 'day')) {
                            return true;
                        }
                    }
                    return false;
                }

                // Standard date hierarchy for other fields: leadDate <= proposalDate <= presentationDate <= winDate <= startDate <= endDate
                // (excluding proposalFollowUpDate from the chain)
                const dateFields = [
                    'leadDate',
                    'proposalDate',
                    'presentationDate',
                    'winDate',
                    'startDate',
                    'endDate'
                ];

                const currentFieldIndex = dateFields.indexOf(currentField);
                if (currentFieldIndex === -1) return false;

                // Check against all preceding fields (must be >= all of them)
                for (let i = 0; i < currentFieldIndex; i++) {
                    const precedingFieldValue = formValues[dateFields[i]];
                    // Ensure the value exists, is a valid dayjs object, and is valid
                    if (precedingFieldValue &&
                        dayjs.isDayjs(precedingFieldValue) &&
                        precedingFieldValue.isValid() &&
                        currentDate.isBefore(precedingFieldValue, 'day')) {
                        return true; // Disable this date because it's before a preceding field
                    }
                }

                // Check against all following fields (must be <= all of them)
                // Special handling: if we're editing proposal-related dates and win date was auto-filled,
                // be more flexible to allow users to adjust the timeline
                for (let i = currentFieldIndex + 1; i < dateFields.length; i++) {
                    const followingFieldValue = formValues[dateFields[i]];
                    const followingFieldName = dateFields[i];

                    // Skip validation against auto-filled dates when editing proposal-related fields OR when editing win date
                    // This allows users to adjust proposal timeline even after setting lead status to 8
                    // and also allows editing win date when it's enabled
                    if ((followingFieldName === 'winDate' || followingFieldName === 'startDate') &&
                        autoFilledFields.has(followingFieldName) &&
                        (['proposalDate', 'presentationDate', 'winDate'].includes(currentField))) {
                        continue; // Skip this constraint to allow editing
                    }

                    // Ensure the value exists, is a valid dayjs object, and is valid
                    if (followingFieldValue &&
                        dayjs.isDayjs(followingFieldValue) &&
                        followingFieldValue.isValid() &&
                        currentDate.isAfter(followingFieldValue, 'day')) {
                        return true; // Disable this date because it's after a following field
                    }
                }

                return false; // Date is valid
            } catch (error) {
                logger.error('Date validation error:', error);
                return false; // If there's an error, don't disable the date
            }
        };
    };

    // Prepare form data for submission - EXACT copy from ClientProjectForm
    const prepareSubmitData = (values, includeNewLostFilter = null) => {
        // Combine leadSource with referralName if applicable
        let leadSourceValue = values.leadSource;
        if ((values.leadSource === 'Referral' || values.leadSource === 'referral') && referralName.trim()) {
            leadSourceValue = `Referral - ${referralName.trim()}`;
        }

        const baseData = omitEmptyProjectStatus({
            ...values,
            leadSource: leadSourceValue,
            startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
            endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
            leadDate: values.leadDate ? values.leadDate.format('YYYY-MM-DD') : null,
            winDate: values.winDate ? values.winDate.format('YYYY-MM-DD') : null,
            proposalDate: values.proposalDate ? values.proposalDate.format('YYYY-MM-DD') : null,
            proposalFollowUpDate: values.proposalFollowUpDate ? values.proposalFollowUpDate.format('YYYY-MM-DD') : null,
            presentationDate: !noPresentation && values.presentationDate ? values.presentationDate.format('YYYY-MM-DD') : null,
            noPresentation,
            noEstimatedVendorCost: noVendorCost,
            estimatedVendorCost: !noVendorCost ? values.estimatedVendorCost : 0,
            // Map plan file to correct database field names
            // Only send the appropriate fields based on which mode is being used
            plan_filepath: values.plan_filepath || null,
            plan_file_url: values.plan_file_url || null,
            plan_filename: values.plan_filename || null,
            crmLeadUrl: values.crmLeadUrl?.trim() || null,
            externalProjectLinks: normalizeEditableExternalProjectLinks(
                values.externalProjectLinks || []
            ),
            // Include hosting service fields
            hostingService: values.hostingService || false,
            domainName: values.domainName || null,
            domainRenewalDate: values.domainRenewalDate ? values.domainRenewalDate.format('YYYY-MM-DD') : null,
            platformName: values.platformName || null,
            platformRenewalDate: values.platformRenewalDate ? values.platformRenewalDate.format('YYYY-MM-DD') : null
        });
        
        // Add lost/rejected form data if lead status is 9
        if (values.lead === 9) {
            // Determine if using new lost filter
            const useNewLostFilter = includeNewLostFilter !== null ? includeNewLostFilter : 
                                     (values.proposalType || values.proposalPresentation || 
                                      values.presentationParticipant || values.withDecisionMaker || 
                                      values.mainReason || values.subReason) ? true : false;
            
            return omitEmptyProjectStatus({
                ...baseData,
                leadFailDate: values.leadFailDate ? values.leadFailDate.format('YYYY-MM-DD') : null,
                leadFailNote: values.leadFailNote || '',
                useNewLostFilter: useNewLostFilter,
                projectLostFilter: useNewLostFilter ? {
                    proposalType: values.proposalType || [],
                    proposalPresentation: values.proposalPresentation || null,
                    presentationParticipant: values.presentationParticipant || [],
                    withDecisionMaker: values.withDecisionMaker || null,
                    mainReason: values.mainReason || null,
                    subReason: values.subReason || null
                } : null,
                leadFail: !useNewLostFilter ? values.leadFail : null
            });
        }
        
        return baseData;
    };

    // Initialize form for create mode
    const initializeCreateForm = (projectType) => {
        if (projectType === 'CLIENT') {
            form.setFieldsValue({
                retainer: 'No',
                lead: 1,
                leadDate: dayjs(),
                probability: 5
            });
            setLeadStatus(1);
            setAutoFilledFields(new Set(['leadDate', 'probability']));
        }
    };

    // Initialize form for edit mode
    const initializeEditForm = (project) => {
        if (!project) return;

        // Parse referral lead source if present
        let leadSourceValue = project.lead_source || project.leadSource;
        if (leadSourceValue && leadSourceValue.startsWith('Referral - ')) {
            setShowReferralInput(true);
            setReferralName(leadSourceValue.substring(11));
            leadSourceValue = 'referral'; // Set dropdown to "referral" (lowercase for EditProjectForm compatibility)
        }

        // Set state from project data
        setLeadStatus(project.lead || null);
        setSelectedClient(project.client?.id || project.client_id || null);
        setNoPresentation(project.no_presentation || false);
        setNoVendorCost(project.no_estimated_vendor_cost || false);

        // Set form values with proper date conversion
        const formData = {
            name: project.name || project.full_name,
            client: project.client?.id || project.client_id || (typeof project.client === 'number' ? project.client : null),
            personInCharge: project.person_in_charge_id || project.person_in_charge?.id || project.personInCharge?.id || project.person_in_charge || project.personInCharge,
            clientPersonInCharge: clientContactIds(project),
            projectTypes: project.classifications || [],
            projectIndustries: project.industries || [],
            lead: project.lead?.id || project.lead_id || project.lead,
            leadSource: leadSourceValue,
            status: project.status,
            probability: project.probability,
            retainer: project.retainer === true || project.retainer === 1 ? 'Yes' : 'No',
            nonBillable: project.non_billable === true || project.non_billable === 1 ? 'Yes' : 'No',
            hostingService: project.hosting_service || project.hostingService || false,
            startDate: parseAccountingDate(project.start_date || project.startDate) || undefined,
            endDate: parseAccountingDate(project.end_date || project.endDate) || undefined,
            leadDate: parseAccountingDate(project.lead_date || project.leadDate) || undefined,
            proposalDate: parseAccountingDate(project.proposal_date || project.proposalDate) || undefined,
            proposalFollowUpDate: parseAccountingDate(project.proposal_follow_up_date || project.proposalFollowUpDate) || undefined,
            presentationDate: parseAccountingDate(project.presentation_date || project.presentationDate) || undefined,
            winDate: parseAccountingDate(project.win_date || project.winDate) || undefined,
            currency: project.currency?.id || project.currency_id || project.currency,
            plannedRevenue: project.planned_revenue || project.plannedRevenue,
            estimatedVendorCost: project.estimated_vendor_cost || project.estimatedVendorCost,
            planURL: project.plan_url || project.planURL,
            planFile: project.plan_filepath || project.plan_file_url || project.planFile,
            plan_filepath: project.plan_filepath,
            plan_file_url: project.plan_file_url,
            plan_filename: project.plan_filename,
            description: project.description,
            brief: project.brief,
            mattermost: project.mattermost,
            crmLeadUrl: project.crm_lead_url || project.crmLeadUrl || '',
            externalProjectLinks: normalizeEditableExternalProjectLinks(
                project.external_project_links || project.externalProjectLinks || []
            ),
            checklists: project.checklists || []
        };
        
        form.setFieldsValue(formData);
    };

    // ========== CONDITIONAL VALIDATION LOGIC ==========
    
    /**
     * Get conditional validation rules based on lead status
     * @param {string} fieldName - Name of the form field
     * @param {Array} defaultRules - Default validation rules for the field
     * @returns {Array} - Validation rules to apply
     */
    const getConditionalRules = useCallback((fieldName, defaultRules = []) => {
        // Get the current lead value from form
        const currentLeadValue = form.getFieldValue('lead');
        
        // If lead status is 9 (Lost/Rejected), skip validation for non-lost fields
        if (currentLeadValue === 9) {
            // Only apply validation to lost/rejected related fields
            if (!LOST_FIELDS.includes(fieldName)) {
                return []; // No validation for non-lost fields when status is Lost
            }
        }
        return defaultRules;
    }, [form]);

    /**
     * Validate form fields conditionally based on lead status
     * @returns {Promise} - Promise that resolves with form values or rejects with validation errors
     */
    const validateConditionally = useCallback(async () => {
        const currentLeadValue = form.getFieldValue('lead');
        
        if (currentLeadValue === 9) {
            // For lost projects, validate only lost fields
            try {
                await form.validateFields(LOST_FIELDS);
                // Get all values including non-validated fields
                return form.getFieldsValue();
            } catch (error) {
                throw new Error('Please fill in the required lost/rejected details');
            }
        } else {
            // For non-lost projects, validate all fields normally
            return await form.validateFields();
        }
    }, [form]);

    /**
     * Handle form submission with conditional validation
     * @param {Function} onSubmit - Callback function to execute after successful validation
     * @returns {Function} - Submit handler function
     */
    const handleConditionalSubmit = useCallback((onSubmit) => {
        return async () => {
            const currentLeadValue = form.getFieldValue('lead');
            
            if (currentLeadValue === 9) {
                // For lost projects, validate only lost fields
                try {
                    await form.validateFields(LOST_FIELDS);
                    const values = form.getFieldsValue();
                    if (onSubmit) {
                        await onSubmit(values);
                    }
                } catch (errorInfo) {
                    logger.debug('Lost field validation failed:', errorInfo);
                    throw new Error('Please fill in the required lost/rejected details');
                }
            } else {
                // For non-lost projects, use normal form submission
                form.submit();
            }
        };
    }, [form]);

    /**
     * Handle form validation failure with conditional logic
     * @param {Function} onSubmit - Callback function to execute if bypassing validation
     * @returns {Function} - Failure handler function
     */
    const handleConditionalFailure = useCallback((onSubmit) => {
        return (errorInfo) => {
            const currentLeadValue = form.getFieldValue('lead');
            
            if (currentLeadValue === 9) {
                // Check if there are only non-lost field errors
                const hasOnlyNonLostErrors = errorInfo.errorFields.every(field => 
                    !LOST_FIELDS.includes(field.name[0])
                );
                
                if (hasOnlyNonLostErrors) {
                    // Bypass validation and submit directly
                    const values = form.getFieldsValue();
                    if (onSubmit) {
                        onSubmit(values);
                    }
                }
            }
        };
    }, [form]);

    /**
     * Trigger re-validation when lead status changes
     * Should be called in the onChange handler of the lead status field
     */
    const triggerRevalidation = useCallback(() => {
        setTimeout(() => {
            form.validateFields();
        }, 100);
    }, [form]);

    /**
     * Handle archive project functionality
     * Shows confirmation modal and archives the project if confirmed
     */
    const handleArchiveProject = useCallback(async (project, form, setIsEditing, onProjectUpdate, setLoading) => {
        const originalStatus = project?.status;

        // Archive eligibility must match the project-detail status selector.
        if (!canArchiveProjectFromDetail(originalStatus)) {
            message.warning('Project can only be archived when status is Finished or Canceled');
            // Revert to current value
            form.setFieldsValue({ status: originalStatus });
            return false;
        }

        // Check vendor bills before allowing archival
        try {
            const vendorCheckResponse = await nodeAPI.get(`/project/${project.id}/vendor-bills/validation`);
            const vendorCheck = vendorCheckResponse.data;

            if (!vendorCheck.canArchive && vendorCheck.missingBills?.length > 0) {
                // Check if user can bypass validation
                const userCanBypass = canBypassArchivalValidation();

                // Format missing bills list
                const missingBillsList = vendorCheck.missingBills.map(bill =>
                    `• ${bill.vendor_name}: ${bill.currency_code || 'USD'} ${(bill.amount_planned || 0).toLocaleString()}`
                ).join('\n');

                if (!userCanBypass) {
                    // User cannot bypass - show blocking error
                    const errorMessage = `Cannot archive project: Missing vendor bills for non-Boost Fee vendors\n\n${missingBillsList}\n\nNote: Boost Fees do not require vendor bills for archival.`;
                    message.error(errorMessage, 10); // Display for 10 seconds
                    form.setFieldsValue({ status: originalStatus });
                    return false;
                }

                // User CAN bypass - show confirmation modal
                const confirmed = await new Promise((resolve) => {
                    AntModal.confirm({
                        title: 'Archive Project with Missing Vendor Bills?',
                        icon: <InfoCircleOutlined />,
                        centered: true,
                        width: 600,
                        content: (
                            <div>
                                <Alert
                                    message="Your role allows bypassing this validation"
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: '16px' }}
                                />
                                <div style={{ marginBottom: '12px' }}>
                                    <strong>Missing Vendor Bills:</strong>
                                </div>
                                <pre style={{
                                    background: '#f5f5f5',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    whiteSpace: 'pre-wrap',
                                    marginBottom: '12px'
                                }}>
                                    {missingBillsList}
                                </pre>
                                <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#8c8c8c' }}>
                                    Note: Boost Fees do not require vendor bills for archival.
                                </div>
                            </div>
                        ),
                        okText: 'Yes, Archive Anyway',
                        okType: 'primary',
                        okButtonProps: {
                            style: {
                                background: '#1890ff',
                                borderColor: '#1890ff'
                            }
                        },
                        cancelText: 'Cancel',
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false)
                    });
                });

                if (!confirmed) {
                    form.setFieldsValue({ status: originalStatus });
                    return false;
                }

                // User confirmed - continue with archival
            }

            // If there was an error checking but we're allowing archival anyway
            if (vendorCheck.warning) {
                logger.warn('Vendor bill check warning:', vendorCheck.warning);
            }
        } catch (error) {
            logger.error('Error checking vendor bills:', error);
            // Don't block archival if the check fails - just log the error
        }

        return new Promise((resolve) => {
            AntModal.confirm({
                title: 'Archive Project?',
                centered: true,
                width: 520,
                content: (
                    <div>
                        <div style={{
                            background: '#f8d7da',
                            border: '1px solid #f5c6cb',
                            borderRadius: '4px',
                            color: '#721c24',
                            padding: '12px 20px',
                            marginBottom: '16px',
                            fontSize: '14px',
                            lineHeight: '1.5'
                        }}>
                            <i className="fa fa-exclamation-circle" style={{ marginRight: '8px', fontSize: '16px' }}></i>
                            <strong>Warning:</strong> Once archived, this project will become <strong>read-only</strong>. 
                            All fields including mandatory fields (client, project name, dates, etc.) will be <strong>permanently locked</strong> and cannot be edited anymore.
                        </div>
                        <p style={{ marginBottom: '0', fontSize: '14px' }}>This action will save immediately and reload the page.</p>
                    </div>
                ),
                okText: "Yes, Archive Project",
                okType: "warning",
                okButtonProps: {
                    style: {
                        background: '#ffc107',
                        borderColor: '#ffc107',
                        color: '#000',
                        fontWeight: '600'
                    }
                },
                cancelText: "Cancel",
                cancelButtonProps: {
                    style: {
                        fontWeight: '500'
                    }
                },
                onOk: async () => {
                    // Save immediately when archiving, bypass validation
                    setLoading(true);
                    try {
                        const response = await archiveProjectViaManagementApi(project.id);
                        
                        if (response.data?.statusCode === 1 || response.status === 200) {
                            message.success('Project archived successfully');
                            // Exit edit mode
                            if (setIsEditing) setIsEditing(false);
                            // Reload project data
                            if (onProjectUpdate) {
                                onProjectUpdate();
                            }
                            resolve(true);
                        } else {
                            message.error('Failed to archive project');
                            // Revert to original value
                            form.setFieldsValue({ status: originalStatus });
                            resolve(false);
                        }
                    } catch (error) {
                        logger.error('Error archiving project:', error);
                        message.error('Failed to archive project');
                        // Revert to original value
                        form.setFieldsValue({ status: originalStatus });
                        resolve(false);
                    } finally {
                        setLoading(false);
                    }
                },
                onCancel: () => {
                    // Revert to original value
                    form.setFieldsValue({ status: originalStatus });
                    resolve(false);
                }
            });
        });
    }, [canBypassArchivalValidation]);

    /**
     * Handle unarchive project functionality
     * Shows confirmation modal and unarchives the project if confirmed
     */
    const handleUnarchiveProject = useCallback(async (project, onProjectUpdate) => {
        return new Promise((resolve) => {
            Modal.confirm({
                title: 'Confirm Unarchive',
                icon: <InfoCircleOutlined />,
                content: (
                    <div>
                        <p>Are you sure you want to unarchive this project?</p>
                        <p style={{ marginBottom: 0, color: '#666', fontSize: '13px' }}>
                            The project will return to its status before archiving. Older projects without archive history will return to Finished.
                        </p>
                    </div>
                ),
                okText: 'Yes, Unarchive',
                okButtonProps: {
                    style: {
                        backgroundColor: '#FED230',
                        borderColor: '#FED230',
                        color: '#000'
                    }
                },
                cancelText: 'Cancel',
                centered: true,
                width: 520,
                onOk: async () => {
                    try {
                        const response = await unarchiveProjectViaManagementApi(project.id);
                        
                        if (response.data?.statusCode === 1 || response.status === 200) {
                            const restoredStatus = response.data?.data?.new_status;
                            message.success(restoredStatus
                                ? `Project restored to ${restoredStatus}`
                                : 'Project unarchived successfully');
                            // Reload project data
                            if (onProjectUpdate) {
                                onProjectUpdate();
                            }
                            resolve(true);
                        } else {
                            message.error('Failed to unarchive project');
                            resolve(false);
                        }
                    } catch (error) {
                        logger.error('Error unarchiving project:', error);
                        message.error('Failed to unarchive project');
                        resolve(false);
                    }
                },
                onCancel: () => {
                    resolve(false);
                }
            });
        });
    }, []);

    /**
     * Validate if project can be set to Finished status
     * Returns an object with isValid boolean and array of validation errors
     */
    /**
     * ALSO USED IN: ProjectDetailNew.js END_DATE_PASSED issue resolver (lines 243-309)
     * If updating this validation, update the issue resolver to match.
     */
    const validateFinishedStatus = useCallback((project, newStatus) => {
        const validationErrors = [];
        
        // Only validate when trying to set status to Finished
        if (newStatus !== 'Finished') {
            return { isValid: true, errors: [] };
        }

        // Check if project type is CONFIRMED
        if (project?.type === 'CONFIRMED') {
            // Check for deliverables (placeholder - implement when API is ready)
            const hasDeliverables = false; // TODO: Implement deliverable check when API is ready
            if (!hasDeliverables) {
                // For now, just show a warning but don't block
                logger.warn('Project should have at least 1 deliverable to be finished');
            }
        }

        // Get field values with multiple name format support
        const clientId = project?.client_id || project?.clientId || project?.client?.id;
        const startDate = project?.start_date || project?.startDate;
        const endDate = project?.end_date || project?.endDate;
        const currencyId = project?.currency_id || project?.currencyId || project?.currency?.id;
        const leadDate = project?.lead_date || project?.leadDate;
        const proposalDate = project?.proposal_date || project?.proposalDate;
        const plannedRevenue = project?.planned_revenue || project?.plannedRevenue;
        const estimatedVendorCost = project?.estimated_vendor_cost || project?.estimatedVendorCost;
        const noEstimatedVendorCost = project?.no_estimated_vendor_cost || project?.noEstimatedVendorCost;
        const planUrl = project?.plan_url || project?.planURL || project?.plan_file_url;
        const projectIndustries = project?.industries || project?.project_industries || project?.projectIndustries;

        // General required fields (all projects except where noted)
        if (!clientId && project?.type !== 'INTERNAL') {
            validationErrors.push('Client is required');
        }
        if (!project?.name) {
            validationErrors.push('Project name is required');
        }
        if (!startDate) {
            validationErrors.push('Start date is required');
        }
        if (!endDate) {
            validationErrors.push('End date is required');
        }
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            validationErrors.push('End date must be after start date');
        }
        if (!currencyId) {
            validationErrors.push('Currency is required');
        }

        // For non-INTERNAL projects
        if (project?.type !== 'INTERNAL') {
            if (!leadDate) {
                validationErrors.push('New lead date is required');
            }
            if (!proposalDate) {
                validationErrors.push('Proposal sent date is required');
            }
            if (!plannedRevenue) {
                validationErrors.push('Estimated project billings is required');
            }
            // Note: Est. agency revenue is auto-calculated from sales documents, not manually entered
        }

        // For non-INTERNAL and non-PROSPECT projects
        if (project?.type !== 'INTERNAL' && project?.type !== 'PROSPECT') {
            if (!estimatedVendorCost && !noEstimatedVendorCost) {
                validationErrors.push('Est. vendor costs is required (or mark as no vendor cost)');
            }
            // GDrive folder URL is not mandatory for lead status 1
            const currentLeadStatus = project?.lead_id || project?.lead || leadStatus;
            if (!planUrl && currentLeadStatus !== 1) {
                validationErrors.push('GDrive folder URL is required');
            }
        }

        // For non-PROSPECT and non-INTERNAL projects
        if (project?.type !== 'PROSPECT' && project?.type !== 'INTERNAL') {
            if (!projectIndustries || (Array.isArray(projectIndustries) && projectIndustries.length === 0)) {
                validationErrors.push('Project industries is required');
            }
        }

        return {
            isValid: validationErrors.length === 0,
            errors: validationErrors
        };
    }, [leadStatus]);

    // Handle proposal type cascade logic
    const handleProposalTypeChange = useCallback((checkedValues, selectedProposalTypes, setSelectedProposalTypes, subReasons, setFilteredSubReasons, formInstance) => {
        const currentValues = [...checkedValues];
        const previousValues = selectedProposalTypes;
        let finalValues = currentValues;
        
        // Handle NO_PROPOSAL mutual exclusion logic
        if (currentValues.includes('NO_PROPOSAL')) {
            if (currentValues.length > 1) {
                // If NO_PROPOSAL is selected along with others, determine what was just clicked
                if (previousValues.includes('NO_PROPOSAL')) {
                    // NO_PROPOSAL was already selected, user clicked another option - remove NO_PROPOSAL
                    finalValues = currentValues.filter(val => val !== 'NO_PROPOSAL');
                } else {
                    // NO_PROPOSAL was just selected - remove all others, keep only NO_PROPOSAL
                    finalValues = ['NO_PROPOSAL'];
                }
            } else {
                // Only NO_PROPOSAL is selected - this is valid
                finalValues = ['NO_PROPOSAL'];
            }
        } else {
            // NO_PROPOSAL is not in current selection
            // If NO_PROPOSAL was previously selected and now other options are selected, it's already removed
            
            // Check if GSLIDE+MOCKUP and GSLIDE are both selected (mutually exclusive)
            if (currentValues.includes('GSLIDE+MOCKUP') && currentValues.includes('GSLIDE')) {
                // Remove the one that was previously selected (keep the most recent)
                if (previousValues.includes('GSLIDE+MOCKUP') && !previousValues.includes('GSLIDE')) {
                    // GSLIDE was just added, remove GSLIDE+MOCKUP
                    finalValues = currentValues.filter(val => val !== 'GSLIDE+MOCKUP');
                } else {
                    // GSLIDE+MOCKUP was just added, remove GSLIDE
                    finalValues = currentValues.filter(val => val !== 'GSLIDE');
                }
            }
        }
        
        // Handle dependent field clearing
        if (finalValues.includes('NO_PROPOSAL')) {
            // Clear dependent fields when NO_PROPOSAL is selected
            formInstance.setFieldsValue({ 
                proposalType: finalValues,
                proposalPresentation: undefined,
                presentationParticipant: [],
                withDecisionMaker: undefined
            });
        } else {
            // Just update proposal type
            formInstance.setFieldsValue({ proposalType: finalValues });
        }
        
        if (finalValues.length === 0) {
            // If no proposal types are selected, clear dependent fields
            formInstance.setFieldsValue({
                proposalPresentation: undefined,
                presentationParticipant: [],
                withDecisionMaker: undefined
            });
        }
        
        // Re-filter sub reasons if main reason is already selected
        const currentMainReason = formInstance.getFieldValue('mainReason');
        if (currentMainReason && subReasons.length > 0) {
            const proposalCondition = finalValues.includes('NO_PROPOSAL') ? 0 : 1;
            const filtered = subReasons.filter(subReason => {
                const parentMatch = subReason.parent_id === currentMainReason || 
                                  subReason.parentId === currentMainReason;
                
                const proposalMatch = subReason.proposal_state === proposalCondition ||
                                    subReason.proposalState === proposalCondition ||
                                    subReason.proposal_state === 2 || 
                                    subReason.proposalState === 2;
                
                return parentMatch && proposalMatch;
            });
            setFilteredSubReasons(filtered);
            
            // Clear sub reason if it's no longer valid
            const currentSubReason = formInstance.getFieldValue('subReason');
            if (currentSubReason && !filtered.find(sr => sr.id === currentSubReason)) {
                formInstance.setFieldsValue({ subReason: undefined });
            }
        }
        
        // Update state to trigger re-render for disabled fields
        setSelectedProposalTypes(finalValues);
    }, []);

    // Handle hosting service cascade logic
    const handleHostingServiceChange = useCallback((value, handleManualFieldChange) => {
        // Mark field as manually changed
        if (handleManualFieldChange) {
            handleManualFieldChange('hostingService');
        }
        
        // Backend will handle clearing hosting details when hostingService is false
    }, []);

    // Handle new lost filter checkbox toggle
    const handleUseNewLostFilterChange = useCallback((checked, setUseNewLostFilter, setLeadFail, setSelectedProposalTypes, formInstance) => {
        setUseNewLostFilter(checked);
        
        // Clear form fields when switching between old and new forms
        if (checked) {
            // Switching to new filter - clear old form fields
            setLeadFail('');
            formInstance.setFieldsValue({ leadFail: '' });
        } else {
            // Switching to old form - clear new form fields
            setSelectedProposalTypes([]);
            formInstance.setFieldsValue({ 
                proposalType: [],
                proposalPresentation: '',
                presentationParticipant: [],
                withDecisionMaker: '',
                mainReason: '',
                subReason: ''
            });
        }
    }, []);

    return {
        // States
        autoFilledFields,
        leadStatus,
        selectedClient,
        noPresentation,
        noVendorCost,
        showReferralInput,
        referralName,

        // State setters
        setNoPresentation,
        setNoVendorCost,
        setAutoFilledFields,
        setLeadStatus,
        setSelectedClient,
        setReferralName,

        // Handlers
        handleManualFieldChange,
        handleClientChange,
        handleLeadChange,
        handleLeadSourceChange,
        handleProposalTypeChange,
        handleHostingServiceChange,
        handleUseNewLostFilterChange,
        
        // Date validation
        validateDisabledDate,
        validateAllDates,
        getDisabledDate,
        
        // Utilities
        prepareSubmitData,
        initializeCreateForm,
        initializeEditForm,
        
        // Conditional validation utilities
        getConditionalRules,
        validateConditionally,
        handleConditionalSubmit,
        handleConditionalFailure,
        triggerRevalidation,
        validateFinishedStatus,
        
        // Archive functions
        handleArchiveProject,
        handleUnarchiveProject,
        
        LOST_FIELDS
    };
};
